<template>
  <div class="book-detail-page" v-loading="loading">
    <!-- Breadcrumb -->
    <div class="breadcrumb-wrapper">
      <el-breadcrumb separator="/">
        <el-breadcrumb-item :to="{ path: '/explore' }">
          {{ $t("nav.explore") }}
        </el-breadcrumb-item>
        <el-breadcrumb-item>{{ $t("bookDetail.detail") }}</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <!-- Top Info Section (orange background) -->
    <div class="book-hero">
      <div class="book-hero-inner">
        <div class="book-cover">
          <img
            v-if="book.cover"
            :src="book.cover"
            :alt="book.title"
            @error="onImgError"
          />
          <div v-else class="cover-placeholder">
            <i class="el-icon-reading"></i>
          </div>
        </div>
        <div class="book-info">
          <p class="book-title">{{ book.title || $t("common.empty") }}</p>
          <p v-if="book.titleEn" class="book-title-en">{{ book.titleEn }}</p>
          <div class="book-meta-grid">
            <div class="meta-item">
              <span class="meta-label">{{ $t("bookDetail.author") }}</span>
              <span class="meta-value">{{ book.author }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">ISBN</span>
              <span class="meta-value">{{ book.isbn }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">{{ $t("bookDetail.publishDate") }}</span>
              <span class="meta-value">{{ book.publishDate }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">{{ $t("bookDetail.pages") }}</span>
              <span class="meta-value">{{ book.pages }}</span>
            </div>
          </div>

          <h3 class="section-label">{{ $t("bookDetail.introduction") }}</h3>
          <p class="book-desc">{{ book.description }}</p>

          <!-- Action Buttons -->
          <div class="action-buttons">
            <el-button
              v-if="book.downloadAddr"
              type="primary"
              round
              size="small"
              icon="el-icon-download"
              @click="onDownload"
            >
              {{ $t("bookDetail.downloadEbook") }}
            </el-button>
            <el-button
              v-if="book.purchaseLink"
              type="primary"
              plain
              round
              size="small"
              icon="el-icon-shopping-cart-2"
              @click="onPurchase"
            >
              {{ $t("bookDetail.purchasePaperBook") }}
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Author Introduction -->
    <div v-if="book.authors && book.authors.length" class="section-wrapper">
      <div class="section-inner">
        <h2 class="section-title">{{ $t("bookDetail.authorIntro") }}</h2>
        <div
          v-for="(author, index) in book.authors"
          :key="author.authorId || index"
          class="author-block"
        >
          <h4 class="author-name">{{ author.name }}</h4>
          <p class="author-bio">{{ author.bio }}</p>
        </div>
      </div>
    </div>

    <!-- Related Books -->
    <div v-if="relatedBooks.length" class="section-wrapper">
      <div class="section-inner">
        <h2 class="section-title">{{ $t("bookDetail.relatedBooks") }}</h2>
        <div class="related-books-grid">
          <div v-for="item in relatedBooks" :key="item.id">
            <book-card :book="item" @click="goToBookDetail(item)" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BookCard from "@/components/BookCard.vue";
import { getImage, formatAuthorName } from "@/utils/tools";
import noImg from "@/assets/images/no-img.png";
import {
  getBookDetail,
  listRelatedBook,
  addReport,
  getBookDownloadUrl,
} from "@/api/book";

export default {
  name: "BookDetailPage",
  components: { BookCard },
  data() {
    return {
      loading: false,
      book: {
        id: null,
        title: "",
        titleEn: "",
        author: "",
        isbn: "",
        publishDate: "",
        pages: "",
        cover: null,
        description: "",
        downloadAddr: "",
        purchaseLink: "",
        categoryIdFirst: null,
        authors: [],
      },
      relatedBooks: [],
    };
  },
  watch: {
    "$route.params.id": {
      immediate: true,
      handler(id) {
        if (id) this.loadDetail(id);
      },
    },
    '$i18n.locale'() {
      this.book.author = formatAuthorName(this.book.authorList, this.book.authorName);
      this.relatedBooks.forEach((book) => {
        book.author = formatAuthorName(book.authorList, book.authorName);
      });
    },
  },
  methods: {
    onImgError(e) {
      e.target.src = noImg;
    },
    async loadDetail(bookId) {
      this.loading = true;
      try {
        const res = await getBookDetail(bookId);
        const data = res?.data || {};
        this.book = {
          id: data.bookId,
          title: data.bookName || "",
          titleEn: data.bookNameEn || "",
          authorList: data.authorList,
          authorName: data.authorName,
          author: formatAuthorName(data.authorList, data.authorName),
          isbn: data.isbn || "",
          publishDate: data.publicationDate || "",
          pages: data.totalPages != null ? String(data.totalPages) : "",
          cover: data.bookImageAddr ? getImage(data.bookImageAddr) : null,
          description: data.bookIntroduction || "",
          downloadAddr: data.downloadAddr || "",
          purchaseLink: data.purchaseLink || "",
          categoryIdFirst: data.categoryIdFirst || null,
          authors: Array.isArray(data.authorList)
            ? data.authorList.map((a) => ({
                authorId: a.authorId,
                name: a.authorName,
                bio: a.authorAbstra,
              }))
            : [],
        };
        this.loadRelated(bookId);
      } catch (err) {
        this.relatedBooks = [];
      } finally {
        this.loading = false;
      }
    },
    async loadRelated(bookId) {
      if (!bookId) {
        this.relatedBooks = [];
        return;
      }
      try {
        const res = await listRelatedBook(bookId);
        const list = Array.isArray(res?.data) ? res.data : [];
        this.relatedBooks = list.map((item) => ({
          id: item.bookId,
          title: item.bookName,
          titleEn: item.bookNameEn,
          authorList: item.authorList,
          authorName: item.authorName,
          author: formatAuthorName(item.authorList, item.authorName),
          date: item.publicationDate,
          cover: item.bookImageAddr ? getImage(item.bookImageAddr) : null,
        }));
      } catch (err) {
        this.relatedBooks = [];
      }
    },
    onDownload() {
      const addr = this.book.downloadAddr;
      if (!addr) return;
      addReport({ bookId: this.book.id, bookName: this.book.title});
      const fullUrl = addr.startsWith("http") ? addr : getBookDownloadUrl(addr);
      window.open(fullUrl, "_blank");
    },
    onPurchase() {
      if (this.book.purchaseLink) {
        window.open(this.book.purchaseLink, "_blank");
      }
    },
    goToBookDetail(book) {
      this.$router.push({ path: `/book/${book.id}` });
    },
  },
};
</script>

<style lang="scss" scoped>
.book-detail-page {
  flex: 1;
  max-width: $max-width;
  width: 100%;
  margin: 0 auto;
}

/* ===== Breadcrumb ===== */
.breadcrumb-wrapper {
  padding: 20px 24px 0;

  ::v-deep .el-breadcrumb {
    font-size: 18px;

    .el-breadcrumb__inner.is-link {
      color: $text-gray;
      font-weight: 400;

      &:hover {
        color: $primary-orange;
      }
    }

    .el-breadcrumb__item:last-child .el-breadcrumb__inner {
      color: $text-dark;
      font-weight: 500;
    }
  }
}

/* ===== Hero Section ===== */
.book-hero {
  padding: 40px 0;
  border-bottom: 1px dashed #dadada;
}

.book-hero-inner {
  max-width: $max-width;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  gap: 36px;
}

.book-cover {
  flex-shrink: 0;
  width: 250px;
  height: 350px;

  img {
    width: 100%;
    height: 100%;
    border-radius: 4px;
    box-shadow: 2px 4px 12px rgba(0, 0, 0, 0.1);
  }

  .cover-placeholder {
    width: 100%;
    aspect-ratio: 3 / 4;
    background: linear-gradient(135deg, #f0e6d6, #e8d5c0);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 2px 4px 12px rgba(0, 0, 0, 0.1);

    i {
      font-size: 40px;
      color: $primary-orange;
      opacity: 0.5;
    }
  }
}

.book-info {
  flex: 1;
  min-width: 0;

  .book-title {
    font-size: 28px;
    font-weight: 500;
    color: $title-color;
    margin-bottom: 8px;
  }

  .book-title-en {
    font-size: 24px;
    color: $title-color;
    font-style: italic;
    margin-bottom: 15px;
  }
}

.book-meta-grid {
  display: grid;
  grid-template-columns: auto auto;
  justify-content: flex-start;
  gap: 8px 40px;
  margin-bottom: 8px;
}

.meta-item {
  .meta-label {
    font-size: 13px;
    color: $text-light;
    margin-right: 8px;
  }

  .meta-value {
    font-size: 13px;
    color: $text-dark;
  }
}

.section-label {
  font-size: 18px;
  color: $text-dark;
  margin: 22px 0 8px;
}

.book-desc {
  font-size: 16px;
  line-height: 1.8;
  color: $text-gray;
}

.action-buttons {
  margin-top: 20px;
  display: flex;
  gap: 12px;
}

/* ===== Content Sections ===== */
.section-wrapper {
  padding-top: 40px;
}

.section-inner {
  max-width: $max-width;
  margin: 0 auto;
  padding: 0 24px;
}

.section-title {
  font-size: 24px;
  font-weight: 700;
  color: $text-dark;
  margin-bottom: 28px;
}

.author-block {
  padding: 20px 30px;
  // background: rgba(254,249,242,0.75);
  background: #f9f2e7;
  border-radius: 5px;

  .author-name {
    font-size: 18px;
    font-weight: 600;
    color: $text-dark;
    margin-bottom: 8px;
  }

  .author-bio {
    font-size: 16px;
    line-height: 1.8;
    color: $text-gray;
    white-space: pre-line;
  }
}

.related-books-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: flex-start;
  margin-bottom: 100px;

  > div {
    flex: 0 0 calc(20% - 16px);
    min-width: 0;
  }
}

@media (max-width: 900px) {
  .book-hero-inner {
    flex-direction: column;
    align-items: center;
    gap: 24px;
  }

  .book-cover {
    width: 200px;
    height: 280px;
  }

  .book-info {
    width: 100%;
    text-align: left;
  }

  .book-meta-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .book-cover {
    width: 180px;
    height: 250px;
  }

  .book-title {
    font-size: 22px;
  }

  .book-title-en {
    font-size: 18px;
  }
}

</style>